import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import generateLOAPDF from "../../utils/generateLOAPDF";

const clean = (v = "") => String(v || "").trim().toLowerCase();

const compactText = (v = "") => {
  if (
    v === null ||
    v === undefined ||
    typeof v === "object" ||
    typeof v === "boolean"
  ) {
    return "";
  }
  return String(v)
    .replace(/\s+/g, " ")
    .replace(/\s+,/g, ",")
    .trim();
};

const firstValue = (...values) => {
  for (const value of values.flat(Infinity)) {
    const result = compactText(value);
    if (result && !["undefined", "null", "n/a", "na", "-", "—"].includes(clean(result))) {
      return result;
    }
  }
  return "";
};

const sameId = (a, b) => Boolean(a && b && String(a) === String(b));

const joinAddress = (...parts) => {
  const seen = new Set();
  return parts
    .flat(Infinity)
    .map(compactText)
    .filter((part) => {
      if (!part || seen.has(clean(part))) return false;
      seen.add(clean(part));
      return true;
    })
    .join(", ");
};

const addressText = (value) => {
  if (!value) return "";
  if (typeof value !== "object") return compactText(value);
  return joinAddress(
    value.houseNo,
    value.shopNo,
    value.officeNo,
    value.building,
    value.addressLine1,
    value.address1,
    value.line1,
    value.addressLine2,
    value.address2,
    value.line2,
    value.area,
    value.landmark,
    value.village,
    value.city,
    value.taluka,
    value.district,
    value.state,
    firstValue(value.pinCode, value.pincode, value.postalCode)
  );
};

const completeRegisteredOffice = (baseAddress, details = {}) => {
  const base = addressText(baseAddress);
  if (!base) return "";

  const baseNeedle = clean(base);
  const missingPart = (value, label = "") => {
    const text = compactText(value);
    if (!text || baseNeedle.includes(clean(text))) return "";
    return label ? `${label}: ${text}` : text;
  };

  return joinAddress(
    base,
    missingPart(details.city),
    missingPart(details.taluka, "Taluka"),
    missingPart(details.district, "District"),
    missingPart(details.state),
    missingPart(firstValue(details.pinCode, details.pincode), "PIN")
  );
};

const numberOnly = (value) => {
  const parsed = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const addDays = (isoDate, days) => {
  if (!isoDate || !Number.isFinite(Number(days))) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + Number(days));
  return date.toISOString().slice(0, 10);
};

function findLinkedGodown(tender = {}, godownList = []) {
  const projectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const applicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
    tender.projectApplicationId,
  ].filter(Boolean);

  const byProjectId = godownList.find((project) =>
    projectIds.some((id) => sameId(project.id, id))
  );
  if (byProjectId) return byProjectId;

  const byApplicationId = godownList.find((project) =>
    applicationIds.some((id) =>
      [project.applicationId, project.godownApplicationId, project.pacsApplicationId]
        .filter(Boolean)
        .some((projectApplicationId) => sameId(projectApplicationId, id))
    )
  );
  if (byApplicationId) return byApplicationId;

  // Legacy tenders without IDs are linked by an exact PACS name only when the
  // match is unique. This prevents a similarly named PACS from another taluka
  // or district being silently selected.
  const tenderPacsName = clean(
    firstValue(
      tender.pacsName,
      tender.societyName,
      tender.organization,
      tender.ownerCompany
    )
  );
  if (!tenderPacsName) return null;

  const nameMatches = godownList.filter(
    (project) =>
      clean(
        firstValue(
          project.pacsName,
          project.societyName,
          project.organizationName,
          project.applicantSocietyName
        )
      ) === tenderPacsName
  );

  if (nameMatches.length === 1) return nameMatches[0];
  if (nameMatches.length < 2) return null;

  const expectedDistrict = clean(
    firstValue(tender.projectDistrict, tender.pacsDistrict, tender.district)
  );
  const expectedTaluka = clean(
    firstValue(tender.projectTaluka, tender.pacsTaluka, tender.taluka)
  );

  const locationMatches = nameMatches.filter((project) => {
    const district = clean(getDistrict(project));
    const taluka = clean(
      firstValue(project.taluka, project.talukaName, project.projectTaluka)
    );
    const districtOk = !expectedDistrict || district === expectedDistrict;
    const talukaOk = !expectedTaluka || taluka === expectedTaluka;
    return districtOk && talukaOk;
  });

  return locationMatches.length === 1 ? locationMatches[0] : null;
}

async function getContractorProfile(uid, lookup = {}) {
  const uidText = compactText(uid);
  const vendorId = firstValue(lookup.vendorId, lookup.contractorVendorId);
  const email = clean(firstValue(lookup.email, lookup.contractorEmail));
  const cacheKey = [uidText, vendorId, email].map(clean).join("|");

  if (cacheKey && getContractorProfile.cache.has(cacheKey)) {
    return getContractorProfile.cache.get(cacheKey);
  }

  // Authoritative Tender Vendor Registration record must be checked before
  // every legacy contractor/profile collection.
  const authoritativeCollections = ["vendors"];

  if (uidText) {
    for (const collectionName of authoritativeCollections) {
      try {
        const snap = await getDoc(doc(db, collectionName, uidText));
        if (snap.exists()) {
          const resolved = {
            id: snap.id,
            ...snap.data(),
            __sourceCollection: collectionName,
            __documentId: snap.id,
          };
          if (cacheKey) getContractorProfile.cache.set(cacheKey, resolved);
          return resolved;
        }
      } catch (error) {
        // Some deployments intentionally deny one or more profile collections.
        console.debug(
          `Contractor profile unavailable in ${collectionName}:`,
          error?.code || error
        );
      }
    }
  }

  // Legacy tender snapshots may contain the human Vendor ID/email but not UID.
  // Resolve only exact, unique matches—never guess from a company name.
  const exactVendorLookups = [
    ["vendorId", vendorId],
    ["auth.email", email],
    ["profile.contactEmail", email],
  ].filter(([, value]) => Boolean(value));

  for (const [field, value] of exactVendorLookups) {
    try {
      const snap = await getDocs(
        query(collection(db, "vendors"), where(field, "==", value))
      );
      if (snap.size === 1) {
        const match = snap.docs[0];
        const resolved = {
          id: match.id,
          ...match.data(),
          __sourceCollection: "vendors",
          __documentId: match.id,
          __resolvedBy: field,
        };
        if (cacheKey) getContractorProfile.cache.set(cacheKey, resolved);
        return resolved;
      }
      if (snap.size > 1) {
        const ambiguous = {
          __lookupError: `Multiple vendor records matched ${field}`,
        };
        if (cacheKey) getContractorProfile.cache.set(cacheKey, ambiguous);
        return ambiguous;
      }
    } catch (error) {
      console.debug(
        `Exact vendor lookup unavailable for ${field}:`,
        error?.code || error
      );
    }
  }

  // Legacy reads are retained only to produce a useful validation message and
  // show the existing tender snapshot. They are never accepted as the final
  // LOA identity when the verified vendors record is missing.
  const legacyCollections = [
    "contractor_profiles",
    "contractors",
    "tender_contractors",
    "nafed_users",
    "users",
  ];

  if (uidText) {
    for (const collectionName of legacyCollections) {
      try {
        const snap = await getDoc(doc(db, collectionName, uidText));
        if (snap.exists()) {
          const resolved = {
            id: snap.id,
            ...snap.data(),
            __sourceCollection: collectionName,
            __documentId: snap.id,
            __lookupError:
              "Verified vendors/{uid} registration not found for awarded contractor",
          };
          if (cacheKey) getContractorProfile.cache.set(cacheKey, resolved);
          return resolved;
        }
      } catch (error) {
        console.debug(
          `Legacy contractor profile unavailable in ${collectionName}:`,
          error?.code || error
        );
      }
    }
  }

  const missing = {
    __lookupError: "Registered vendor record not found for awarded contractor",
  };
  if (cacheKey) getContractorProfile.cache.set(cacheKey, missing);
  return missing;
}

getContractorProfile.cache = new Map();

function resolveTenderRecord(tender = {}, project = {}, contractorProfile = {}) {
  const awardedVendor =
    tender.awardedVendorDetails ||
    tender.awardedVendor ||
    tender.l1Vendor ||
    tender.selectedVendor ||
    {};

  const contractorAccount = contractorProfile || {};
  const nestedProfile =
    contractorAccount.profile && typeof contractorAccount.profile === "object"
      ? contractorAccount.profile
      : contractorAccount.vendorProfile &&
        typeof contractorAccount.vendorProfile === "object"
      ? contractorAccount.vendorProfile
      : {};
  const profile = { ...contractorAccount, ...nestedProfile };
  const vendorIdentity =
    contractorAccount.loaIdentity &&
    typeof contractorAccount.loaIdentity === "object"
      ? contractorAccount.loaIdentity
      : {};
  const vendorAuth =
    contractorAccount.auth && typeof contractorAccount.auth === "object"
      ? contractorAccount.auth
      : {};
  const contractorProfileSource =
    contractorAccount.__sourceCollection || "";
  const contractorRegistrationStatus = clean(contractorAccount.status);
  const contractorProfileVerified =
    contractorProfileSource === "vendors" &&
    ["approved", "active"].includes(contractorRegistrationStatus);
  const pacs =
    project.pacsDetails ||
    project.pacs ||
    project.applicantDetails ||
    project.applicant ||
    project.applicationData ||
    {};
  const location =
    project.locationDetails ||
    project.siteDetails ||
    project.projectLocationDetails ||
    {};
  const contractorBusiness =
    awardedVendor.businessDetails ||
    awardedVendor.companyDetails ||
    profile.businessDetails ||
    profile.companyDetails ||
    profile.contractorDetails ||
    {};

  const registeredVendorHeadOffice = firstValue(
    vendorIdentity.headOfficeAddress,
    profile.headOfficeAddress
  );
  const registeredVendorAddress = firstValue(
    vendorIdentity.registeredOffice,
    completeRegisteredOffice(registeredVendorHeadOffice, {
      city: firstValue(vendorIdentity.city, profile.city),
      taluka: firstValue(vendorIdentity.taluka, profile.taluka),
      district: firstValue(vendorIdentity.district, profile.district),
      state: firstValue(vendorIdentity.state, profile.state),
      pinCode: firstValue(
        vendorIdentity.pinCode,
        vendorIdentity.pincode,
        profile.pinCode,
        profile.pincode
      ),
    })
  );
  const pacsName = firstValue(
    project.pacsName,
    project.societyName,
    project.organizationName,
    project.applicantSocietyName,
    pacs.pacsName,
    pacs.societyName,
    pacs.nameOfSociety,
    pacs.organizationName,
    pacs.legalName,
    pacs.name,
    tender.pacsName,
    tender.societyName,
    tender.organization,
    tender.ownerCompany
  );

  const state = firstValue(
    project.state,
    project.stateName,
    project.projectState,
    project.pacsState,
    location.state,
    pacs.state,
    tender.projectState,
    tender.pacsState,
    tender.state
  );

  const district = firstValue(
    project.district,
    project.districtName,
    project.projectDistrict,
    project.pacsDistrict,
    location.district,
    location.districtName,
    pacs.district,
    tender.projectDistrict,
    tender.pacsDistrict,
    tender.district
  );

  const taluka = firstValue(
    project.taluka,
    project.talukaName,
    project.projectTaluka,
    project.pacsTaluka,
    location.taluka,
    location.talukaName,
    pacs.taluka,
    tender.projectTaluka,
    tender.pacsTaluka,
    tender.taluka
  );

  const village = firstValue(
    project.village,
    project.villageName,
    project.projectVillage,
    project.pacsVillage,
    location.village,
    location.villageName,
    pacs.village,
    tender.projectVillage,
    tender.pacsVillage,
    tender.village
  );

  const pinCode = firstValue(
    project.pinCode,
    project.pincode,
    project.postalCode,
    location.pinCode,
    location.pincode,
    pacs.pinCode,
    pacs.pincode,
    tender.projectPinCode,
    tender.pacsPinCode
  );

  const explicitSiteAddress = firstValue(
    addressText(project.siteAddress),
    addressText(project.projectAddress),
    addressText(project.projectLocation),
    addressText(project.pacsAddress),
    addressText(project.fullAddress),
    addressText(location.address),
    addressText(location.siteAddress),
    addressText(pacs.address),
    addressText(pacs.registeredOfficeAddress),
    addressText(tender.siteAddress),
    addressText(tender.projectAddress),
    addressText(tender.projectLocation),
    addressText(tender.pacsAddress)
  );

  const explicitSiteNeedle = clean(explicitSiteAddress);
  const missingFromExplicit = (value) =>
    Boolean(value && (!explicitSiteNeedle || !explicitSiteNeedle.includes(clean(value))));
  const projectLocation = joinAddress(
    explicitSiteAddress,
    missingFromExplicit(village) && `At/Post: ${village}`,
    missingFromExplicit(taluka) && `Taluka: ${taluka}`,
    missingFromExplicit(district) && `District: ${district}`,
    missingFromExplicit(state) && state,
    missingFromExplicit(pinCode) && `PIN: ${pinCode}`
  );

  const contractorName = firstValue(
    vendorIdentity.legalName,
    profile.companyName,
    profile.legalName,
    profile.firmName,
    profile.organizationName,
    profile.businessName,
    tender.awardedVendorName,
    tender.l1ContractorName,
    awardedVendor.legalName,
    awardedVendor.firmName,
    awardedVendor.companyName,
    awardedVendor.name,
    contractorBusiness.legalName,
    contractorBusiness.firmName,
    contractorBusiness.companyName,
    contractorBusiness.businessName,
    profile.displayName,
    profile.name,
    tender.contractorName,
    tender.vendorName
  );

  const explicitContractorAddress = firstValue(
    registeredVendorAddress,
    completeRegisteredOffice(profile.registeredOfficeAddress, profile),
    completeRegisteredOffice(profile.officeAddress, profile),
    completeRegisteredOffice(profile.fullAddress, profile),
    completeRegisteredOffice(profile.address, profile),
    addressText(tender.awardedVendorAddress),
    addressText(tender.l1ContractorAddress),
    addressText(awardedVendor.registeredOfficeAddress),
    addressText(awardedVendor.officeAddress),
    addressText(awardedVendor.fullAddress),
    addressText(awardedVendor.address),
    addressText(contractorBusiness.registeredOfficeAddress),
    addressText(contractorBusiness.officeAddress),
    addressText(contractorBusiness.address),
    addressText(tender.contractorRegisteredOffice),
    addressText(tender.contractorAddress),
    addressText(tender.vendorAddress)
  );

  const contractorAddress = explicitContractorAddress || joinAddress(
    firstValue(
      awardedVendor.addressLine1,
      contractorBusiness.addressLine1,
      profile.addressLine1,
      profile.address1
    ),
    firstValue(
      awardedVendor.addressLine2,
      profile.addressLine2,
      profile.address2,
      contractorBusiness.addressLine2
    ),
    firstValue(
      awardedVendor.city,
      contractorBusiness.city,
      profile.city,
      tender.city
    ),
    firstValue(awardedVendor.taluka, contractorBusiness.taluka, profile.taluka),
    firstValue(
      awardedVendor.district,
      contractorBusiness.district,
      profile.district
    ),
    firstValue(awardedVendor.state, contractorBusiness.state, profile.state),
    firstValue(
      awardedVendor.pinCode,
      awardedVendor.pincode,
      contractorBusiness.pinCode,
      contractorBusiness.pincode,
      profile.pinCode,
      profile.pincode
    )
  );

  const capacityMt = numberOnly(
    firstValue(
      project.capacityMt,
      project.godownCapacity,
      project.projectCapacity,
      tender.capacityMt,
      tender.godownCapacity,
      tender.projectCapacity,
      tender.capacity
    )
  );

  return {
    ...tender,
    linkedGodownId: project.id || tender.linkedGodownId || "",
    linkedGodown: project,
    contractorProfile: profile,
    pacsName,
    pacsRegistrationNumber: firstValue(
      project.registrationNumber,
      project.registrationNo,
      project.pacsRegistrationNumber,
      project.societyRegistrationNo,
      pacs.registrationNumber,
      pacs.registrationNo,
      pacs.societyRegistrationNo,
      tender.pacsRegistrationNumber
    ),
    capacityMt,
    state,
    district,
    taluka,
    village,
    pinCode,
    projectLocation,
    projectAddress: projectLocation,
    contractorName,
    contractorAddress,
    contractorRegisteredOffice: contractorAddress,
    contractorHeadOfficeAddress: registeredVendorHeadOffice,
    contractorProfileSource,
    contractorRegistrationStatus,
    contractorProfileVerified,
    contractorProfileError: contractorAccount.__lookupError || "",
    contractorVendorId: firstValue(
      contractorAccount.vendorId,
      vendorIdentity.vendorId,
      tender.awardedVendorId,
      tender.contractorVendorId
    ),
    contractorCompanyType: firstValue(
      vendorIdentity.companyType,
      profile.companyType
    ),
    contractorDistrict: firstValue(vendorIdentity.district, profile.district),
    contractorState: firstValue(vendorIdentity.state, profile.state),
    contractorPinCode: firstValue(
      vendorIdentity.pinCode,
      vendorIdentity.pincode,
      profile.pinCode,
      profile.pincode
    ),
    contractorCin: firstValue(vendorIdentity.cin, profile.cin),
    contractorUdyam: firstValue(vendorIdentity.udyam, profile.udyam),
    contractorClass: firstValue(
      vendorIdentity.contractorClass,
      profile.contractorClass
    ),
    contractorDesignation: firstValue(
      tender.awardedVendorDesignation,
      awardedVendor.authorizedSignatoryDesignation,
      awardedVendor.designation,
      contractorBusiness.authorizedSignatoryDesignation,
      contractorBusiness.designation,
      profile.authorizedSignatoryDesignation,
      profile.designation,
      "Authorized Signatory"
    ),
    contractorAuthorizedSignatory: firstValue(
      vendorIdentity.contactName,
      profile.contactName,
      tender.awardedVendorAuthorizedSignatory,
      awardedVendor.authorizedSignatoryName,
      awardedVendor.contactPerson,
      contractorBusiness.authorizedSignatoryName,
      contractorBusiness.contactPerson,
      profile.authorizedSignatoryName,
      profile.contactPerson
    ),
    contractorGstin: firstValue(
      vendorIdentity.gstin,
      profile.gst,
      tender.awardedVendorGstin,
      awardedVendor.gstin,
      contractorBusiness.gstin,
      contractorBusiness.gstNumber,
      profile.gstin,
      profile.gstNumber
    ),
    contractorPan: firstValue(
      vendorIdentity.pan,
      profile.pan,
      awardedVendor.pan,
      contractorBusiness.pan,
      contractorBusiness.panNumber,
      profile.pan,
      profile.panNumber
    ),
    contractorEmail: firstValue(
      vendorIdentity.email,
      profile.contactEmail,
      vendorAuth.email,
      tender.awardedVendorEmail,
      awardedVendor.email,
      contractorBusiness.email,
      profile.email
    ),
    contractorMobile: firstValue(
      vendorIdentity.mobile,
      profile.contactPhone,
      tender.awardedVendorMobile,
      awardedVendor.mobile,
      awardedVendor.phone,
      contractorBusiness.mobile,
      contractorBusiness.phone,
      profile.mobile,
      profile.phone
    ),
    dataSource:
      project.id && contractorProfileSource === "vendors"
        ? "Linked Godown Project + Registered Vendor Profile"
        : project.id
        ? "Linked Godown Project + Unverified Contractor Snapshot"
        : contractorProfileSource === "vendors"
        ? "Tender + Registered Vendor Profile"
        : "Unverified Tender Snapshot",
  };
}

const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    ""
  );
}

function isStrictNafedProject(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeToProject(project, nafedScope) {
  if (!nafedScope?.onlyNafedApply && !nafedScope?.strictNafedApplyOnly) {
    return true;
  }

  const scopeState = clean(nafedScope?.state);
  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const stateOk = !scopeState || clean(getState(project)) === scopeState;
  const districtOk =
    !allowedDistricts.length ||
    allowedDistricts.includes(clean(getDistrict(project)));

  return isStrictNafedProject(project) && stateOk && districtOk;
}

function tenderMatchesAssignedProject(tender, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
    tender.projectApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function Info({ title, value }) {
  return (
    <div className="loa-info">
      <label>{title}</label>
      <b>{value || "—"}</b>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="loa-stat">
      <label>{label}</label>
      <b>{value}</b>
    </div>
  );
}

function Badge({ type = "pending", children }) {
  return <span className={`loa-badge ${type}`}>{children}</span>;
}

const PMC_LEGAL_NAME =
  "Shine Agri Allied & Marketing Multistate Cooperative Society Limited";
const PMC_REGISTERED_OFFICE =
  "48, 1st Amar Arcade, Nilpur, Taluka Dantiwada, District Banaskantha, Gujarat - 385505";
const PMC_REGISTRATION_NO = "MSCS/CR/1572/2024";
const LOA_TERMS_VERSION = "PMC-CONTRACTOR-LOA-2026.09";

function buildPmcProtectionClauses(data = {}, settings = {}) {
  const pmc = data.pmcName || PMC_LEGAL_NAME;
  const contractor = data.contractorName || "the Contractor";
  const beneficiary = data.pacsName || "the Project Beneficiary / PACS";
  const duration = Number(data.workDurationDays || 0);
  const dlp = Number(data.defectLiabilityMonths || 12);
  const ldRate = Number(settings.liquidatedDamagesPerWeekPercent ?? 0.5);
  const ldCap = Number(settings.liquidatedDamagesCapPercent ?? 10);
  const cureDays = Number(settings.defaultCurePeriodDays || 7);
  const claimNoticeDays = Number(settings.claimNoticeDays || 7);

  return [
    {
      no: 1,
      title: "Capacity and relationship of parties",
      text:
        pmc +
        " issues this LOA in its disclosed capacity as Project Management Consultant and authorised implementing/certifying authority for " +
        beneficiary +
        ". " +
        contractor +
        " is engaged as an independent turnkey contractor and shall have no authority to bind the PMC, the PACS, NAFED, any bank or any government authority. No partnership, agency, employment, joint venture or fiduciary relationship is created.",
    },
    {
      no: 2,
      title: "Complete turnkey responsibility",
      text:
        "The Contractor accepts single-point responsibility for survey verification, setting-out, temporary works, labour, supervision, plant, materials, fabrication, transport, unloading, storage, execution, testing, commissioning, statutory compliance, rectification, as-built records and safe handover of the complete functional facility in accordance with the tender, sanctioned drawings, BOQ and written PMC directions.",
    },
    {
      no: 3,
      title: "Site and document due diligence",
      text:
        "Before acceptance and mobilisation, the Contractor shall independently inspect the site, access, levels, dimensions, soil/report data made available, utilities, local conditions and all tender documents. Any discrepancy, omission or ambiguity shall be notified in writing before executing the affected work. Silence or execution constitutes acceptance of the available conditions; however, concealed physical conditions that could not reasonably be discovered shall be dealt with only through a written PMC determination.",
    },
    {
      no: 4,
      title: "Order of precedence",
      text:
        "In case of conflict, the order of precedence shall be: signed amendment/change order; this LOA and special conditions; written clarifications/addenda; technical specifications; approved drawings; BOQ; tender conditions; accepted bid. The stricter quality or safety requirement shall apply unless the PMC gives a written determination. The Contractor shall not rely on oral instructions.",
    },
    {
      no: 5,
      title: "Fixed rates and taxes",
      text:
        "Accepted rates are firm for the entire completion and rectification period and include all labour, materials, leads/lifts, wastage, freight, loading/unloading, tools, testing, insurance, royalties, cess, duties and incidental costs, except GST only to the extent expressly shown and lawfully payable. No escalation or idle-charge claim is admissible unless approved through a prior written change order signed by the authorised PMC signatory.",
    },
    {
      no: 6,
      title: "No variation without written change order",
      text:
        "Extra, substituted or deviated work shall not create any entitlement unless its scope, rate and time effect are approved in writing before execution by the authorised PMC signatory. Site conversations, messages, drawings marked for discussion, engineer presence, measurement or use of work shall not by themselves constitute approval or waiver.",
    },
    {
      no: 7,
      title: "Programme, progress and delay notice",
      text:
        "Time is material. The Contractor shall mobilise and complete within " +
        duration +
        " days in accordance with the approved baseline programme, submit weekly look-ahead plans, geo-tagged progress photographs and updated recovery programmes. Any event likely to affect time or cost must be notified with contemporary particulars within " +
        claimNoticeDays +
        " days, followed by full substantiation; failing timely notice, the claim may be rejected to the extent the delay prejudices verification or mitigation.",
    },
    {
      no: 8,
      title: "Liquidated damages",
      text:
        "For culpable delay beyond the contractual completion date, the PMC may levy reasonable liquidated damages at " +
        ldRate +
        "% of the accepted contract value per completed week or part thereof, capped at " +
        ldCap +
        "% of the accepted contract value. The parties record this as a genuine pre-estimate of likely administrative and project loss, subject to applicable law. This does not permit double recovery for the same loss or excuse completion and rectification obligations.",
    },
    {
      no: 9,
      title: "Quality control and rejection",
      text:
        "Only approved makes and conforming materials shall be used. The Contractor shall provide samples, manufacturer certificates and third-party tests when directed. Inspection, measurement, certification or interim payment does not relieve the Contractor of responsibility. The PMC may reject, uncover, test, remove and require replacement of defective or unapproved work at the Contractor's risk and cost.",
    },
    {
      no: 10,
      title: "Safety, labour and statutory compliance",
      text:
        "The Contractor is solely responsible for site safety, barricading, permits, trained supervision, labour welfare, wages, EPF, ESIC, BOCW cess/compliance, licences, environmental controls and compliance with all applicable laws. The Contractor shall immediately report incidents and indemnify protected parties against consequences of its breach, subject to applicable law.",
    },
    {
      no: 11,
      title: "Insurance",
      text:
        "Before mobilisation and throughout execution, the Contractor shall maintain appropriate Contractors' All Risk/erection insurance, workmen/employees compensation, third-party liability, plant/equipment and motor insurance with adequate limits, name the PMC and beneficiary as additional insured/loss payee where available, and provide policies and renewals. Insurance does not limit contractual liability.",
    },
    {
      no: 12,
      title: "Performance security, retention and set-off",
      text:
        "Performance security and retention shall remain valid and enforceable until expiry of the applicable defect-liability obligations and settlement of claims. The PMC may, after written particulars, set off or recover amounts due from EMD, security, retention, RA/final bills or any other amount payable under this project. Such adjustment shall not prevent recovery of any lawful balance.",
    },
    {
      no: 13,
      title: "Measurements, bills and certification",
      text:
        "Payment is due only for measured, accepted and certified work. Every bill shall include the GST invoice, measurement records, test reports, material approvals, photographs, statutory declarations and other required documents. PMC certification is an assessment of eligible work and is not acceptance of defects or a waiver. Final payment does not discharge latent defects, fraud, indemnities, warranties or unresolved recoveries.",
    },
    {
      no: 14,
      title: "Project-fund administration and no personal liability",
      text:
        "The Contractor acknowledges that project payments are administered against funds released for the project by the beneficiary/PACS, bank, financing institution or scheme authority. Unless the PMC expressly undertakes otherwise in a separately signed instrument, the PMC is not required to finance the works from its own funds and its office-bearers, employees and consultants incur no personal liability. The PMC shall process duly certified claims in accordance with the contract, subject always to non-waivable statutory payment rights.",
    },
    {
      no: 15,
      title: "Title, custody and risk",
      text:
        "Materials and work paid for shall vest in the project/beneficiary to the extent of payment, but care, custody and all risk of loss, theft, deterioration or damage remain with the Contractor until final handover. The Contractor shall not create any lien, charge or third-party right over the site, materials, work or project documents.",
    },
    {
      no: 16,
      title: "Subcontracting and assignment",
      text:
        "The Contractor shall not assign the contract or subcontract any substantial or specialised part without prior written PMC consent. Consent does not release the Contractor, which remains fully liable for every subcontractor, supplier and worker as for its own acts and omissions.",
    },
    {
      no: 17,
      title: "Defect liability and warranties",
      text:
        "The defect-liability period is " +
        dlp +
        " months from the project completion certificate, or such longer period stated for a specific item in the tender/BOQ/manufacturer warranty. On notice, the Contractor shall promptly rectify at its cost; failing which the PMC may arrange rectification and recover the reasonable cost. Rectified/replaced work carries a fresh defect period to the extent permitted by the contract. Latent defects, fraud and deliberate concealment remain governed by applicable law.",
    },
    {
      no: 18,
      title: "Suspension, step-in and termination",
      text:
        "For material breach, slow progress, defective work or non-compliance, the PMC may issue a cure notice of " +
        cureDays +
        " days and, if not cured, suspend work, take protective possession of project materials/documents, engage others, terminate the whole or part and recover reasonable excess completion and rectification cost. Immediate protective action or termination may be taken for abandonment, fraud, bribery, serious safety danger, insolvency or unlawful conduct, subject to applicable law.",
    },
    {
      no: 19,
      title: "Indemnity",
      text:
        "The Contractor shall defend, indemnify and hold harmless the PMC, beneficiary/PACS, their officers, employees and consultants from third-party claims, death/injury, property damage, labour/statutory dues, taxes, infringement, pollution and direct loss arising from the Contractor's breach, negligence, wilful misconduct or violation of law. The indemnity does not apply to the extent finally caused by the protected party's own gross negligence or wilful misconduct.",
    },
    {
      no: 20,
      title: "Force majeure",
      text:
        "Force majeure is limited to an unforeseeable event beyond reasonable control that directly prevents performance despite mitigation. The affected party shall notify within 3 days, provide evidence, protect the works and resume promptly. Shortage of funds, market price increase, ordinary monsoon, labour/subcontractor default or material procurement delay is not force majeure unless directly caused by a qualifying event. Relief is time only unless a written change order expressly allows cost.",
    },
    {
      no: 21,
      title: "Anti-bribery, conflict and records",
      text:
        "The Contractor shall not offer any commission, gift, facilitation payment or improper benefit; shall disclose conflicts and related parties; and shall keep authentic procurement, wage, tax, test and project records available for audit. Material misrepresentation, forged records, collusion or bribery is a fundamental breach permitting immediate termination, recovery and reporting to competent authorities.",
    },
    {
      no: 22,
      title: "Confidentiality and project data",
      text:
        "Tender rates, drawings, personal data, credentials, reports and non-public project information shall be used only for this project, protected with reasonable safeguards and disclosed only as authorised or required by law. All project-specific drawings, reports, photographs and as-built records prepared and paid for under the contract shall be delivered to the PMC and may be used for construction, operation, repair and audit of the project.",
    },
    {
      no: 23,
      title: "Claims and dispute escalation",
      text:
        "A claim shall first be submitted with facts, contractual basis, calculations and supporting records to the PMC's authorised representative. Senior representatives shall attempt resolution within 15 days. Pending resolution, the Contractor shall continue undisputed and safe work and both parties shall perform undisputed obligations, unless the contract is lawfully terminated or performance is impossible.",
    },
    {
      no: 24,
      title: "Arbitration, seat and courts",
      text:
        "Unresolved disputes shall be referred to a sole arbitrator mutually appointed by the parties; failing agreement, appointment shall be sought under the Arbitration and Conciliation Act, 1996. The juridical seat and venue shall be " +
        (data.jurisdiction || "Palanpur, District Banaskantha, Gujarat") +
        ", proceedings shall be in English, and the award shall be reasoned. Courts having jurisdiction over the stated seat shall have exclusive supervisory jurisdiction, subject to mandatory law.",
    },
    {
      no: 25,
      title: "Electronic records and acceptance",
      text:
        "Portal issuance, authenticated login, digitally/electronically signed acceptance, signed upload, verified email acknowledgement and system audit logs may be relied upon as electronic records. Mere generation of the LOA without dispatch/portal availability does not prove contractor acceptance; the recorded acceptance event and timestamp shall be preserved.",
    },
    {
      no: 26,
      title: "Entire agreement, no waiver and severability",
      text:
        "The contract documents constitute the entire agreement for the awarded work. Amendment or waiver is valid only when written and signed by an authorised representative. Delay or partial exercise of a right is not a waiver. If a provision is unenforceable, it shall be read down or severed to the minimum extent and the remaining provisions continue. Accrued payment, recovery, confidentiality, audit, indemnity, warranty and dispute provisions survive completion or termination.",
    },
  ];
}

function validateLOAData(data = {}) {
  const required = [
    ["PACS / Society name", data.pacsName],
    ["project capacity", data.projectCapacity],
    ["project village / site", data.projectLocation],
    ["taluka", data.taluka],
    ["district", data.district],
    ["state", data.state],
    ["contractor legal name", data.contractorName],
    ["contractor registered-office address", data.contractorAddress],
  ];

  const errors = required
    .filter(([, value]) => !compactText(value))
    .map(([label]) => `Missing ${label}`);

  if (clean(data.pacsName) === clean(data.contractorName)) {
    errors.push("PACS and Contractor names are identical; source mapping is incorrect");
  }

  if (
    data.contractorAddress &&
    data.projectLocation &&
    clean(data.contractorAddress) === clean(data.projectLocation)
  ) {
    errors.push("Contractor registered office and project site are identical; verify source mapping");
  }

  if (!data.contractorUid) {
    errors.push("Awarded contractor UID is missing from the tender");
  }

  if (data.contractorProfileSource !== "vendors") {
    errors.push(
      data.contractorProfileError ||
        "Awarded contractor is not linked to its verified vendors/{uid} registration"
    );
  } else if (data.contractorProfileVerified !== true) {
    errors.push(
      `Vendor registration status must be approved/active (current: ${
        data.contractorRegistrationStatus || "missing"
      })`
    );
  }

  if (!compactText(data.contractorVendorId)) {
    errors.push("Registered Vendor ID is missing");
  }

  if (compactText(data.contractorHeadOfficeAddress).length < 10) {
    errors.push("Vendor registration does not contain a complete Head Office Address");
  }

  if (compactText(data.contractorAddress).length < 15) {
    errors.push("Contractor registered-office address is too short or incomplete");
  }

  if (!/^\d{6}$/.test(compactText(data.contractorPinCode))) {
    errors.push("Contractor registered-office PIN code is missing or invalid");
  }

  if (!compactText(data.contractorEmail).includes("@")) {
    errors.push("Registered contractor email is missing or invalid");
  }

  if (!/^\d{10}$/.test(compactText(data.contractorMobile).replace(/\D/g, ""))) {
    errors.push("Registered contractor mobile number is missing or invalid");
  }

  if (!data.linkedGodownId) {
    errors.push("Tender is not uniquely linked to a godown project/application");
  }

  if (!Number.isFinite(Number(data.l1Amount)) || Number(data.l1Amount) <= 0) {
    errors.push("Award amount is missing or invalid");
  }

  if (!Number.isFinite(Number(data.workDurationDays)) || Number(data.workDurationDays) <= 0) {
    errors.push("Work duration is missing or invalid");
  }

  return errors;
}

export default function GenerateLOA({ nafedScope }) {
  const [tenders, setTenders] = useState([]);
  const [selectedTenderId, setSelectedTenderId] = useState("");
  const [selectedTender, setSelectedTender] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [generateDate, setGenerateDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [durationDays, setDurationDays] = useState(180);
  const [loaRegister, setLoaRegister] = useState([]);
  const [search, setSearch] = useState("");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  useEffect(() => {
    fetchTenders();
    fetchSettings();
    fetchLoaRegister();
  }, [JSON.stringify(nafedScope || {})]);

  const getAssignedProjects = async () => {
    const godownSnap = await getDocs(collection(db, "godown_projects"));

    const assigned = godownSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => applyNafedScopeToProject(p, nafedScope));

    const projectIds = new Set(assigned.map((p) => String(p.id)));

    const applicationIds = new Set(
      assigned.map((p) => String(p.applicationId || "")).filter(Boolean)
    );

    return { assigned, projectIds, applicationIds };
  };

  const fetchTenders = async () => {
    try {
      const tenderSnap = await getDocs(collection(db, "tenders"));
      const godownSnap = await getDocs(collection(db, "godown_projects"));

      const godownList = godownSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      let assignedProjectIds = new Set();
      let assignedApplicationIds = new Set();

      if (isNafedMode) {
        const scope = await getAssignedProjects();
        assignedProjectIds = scope.projectIds;
        assignedApplicationIds = scope.applicationIds;
      }

      let list = tenderSnap.docs.map((d) => {
        const tender = { id: d.id, ...d.data() };
        const linkedGodown = findLinkedGodown(tender, godownList) || {};
        const resolved = resolveTenderRecord(tender, linkedGodown);

        return {
          ...resolved,
          nafedProject:
            tender.nafedProject === true || linkedGodown.nafedProject === true,
          projectSource:
            tender.projectSource || linkedGodown.projectSource || "",
          applicationPortal:
            tender.applicationPortal || linkedGodown.applicationPortal || "",
        };
      });

      const awardedList = list.filter(
        (t) =>
          t.awardedVendorUid ||
          t.awardedAmount ||
          t.awardedValue ||
          t.l1Amount ||
          t.status === "L1 Final" ||
          t.status === "Awarded"
      );

      let finalList = awardedList;

      if (isNafedMode) {
        const scopeState = clean(nafedScope?.state);
        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        finalList = awardedList.filter((t) => {
          const linked = tenderMatchesAssignedProject(
            t,
            assignedProjectIds,
            assignedApplicationIds
          );

          const strictTender =
            t.nafedProject === true && t.projectSource === "NAFED";

          const stateOk = !scopeState || clean(getState(t)) === scopeState;

          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(t)));

          return linked || (strictTender && stateOk && districtOk);
        });
      }

      // Resolve the contractor's registered-office details separately from
      // project/PACS details. The old code treated tender.city as the project
      // district, which caused mixed locations such as Dabhoda + Vav-Tharad.
      finalList = await Promise.all(
        finalList.map(async (tender) => {
          const contractorUid = firstValue(
            tender.awardedVendorUid,
            tender.l1VendorUid,
            tender.contractorUid,
            tender.vendorUid,
            tender.awardedVendorDetails?.uid,
            tender.awardedVendor?.uid
          );
          const profile = await getContractorProfile(contractorUid, {
            vendorId: firstValue(
              tender.awardedVendorId,
              tender.contractorVendorId,
              tender.l1VendorId,
              tender.vendorId,
              tender.awardedVendorDetails?.vendorId,
              tender.awardedVendor?.vendorId
            ),
            email: firstValue(
              tender.awardedVendorEmail,
              tender.contractorEmail,
              tender.awardedVendorDetails?.email,
              tender.awardedVendor?.email
            ),
          });
          return resolveTenderRecord(
            {
              ...tender,
              awardedVendorUid:
                contractorUid ||
                (profile.__sourceCollection === "vendors" ? profile.id : ""),
            },
            tender.linkedGodown || {},
            profile
          );
        })
      );

      finalList.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );

      setTenders(finalList);
    } catch (e) {
      console.error("Tender loading failed:", e);
      alert("Tender loading failed.");
    }
  };

  const fetchSettings = async () => {
    const refDoc = doc(db, "loa_settings", "default");
    const snap = await getDoc(refDoc);

    if (snap.exists()) {
      setSettings(snap.data());
      setDurationDays(snap.data()?.workDurationDays || 180);
    } else {
      setSettings({
        performanceSecurity: 250000,
        workDurationDays: 180,
        defectLiabilityMonths: 12,
        jurisdiction: "Palanpur, District Banaskantha, Gujarat",
        paymentReleaseDays: 10,
        raBillMinimum: 525000,
        liquidatedDamagesPerWeekPercent: 0.5,
        liquidatedDamagesCapPercent: 10,
        defaultCurePeriodDays: 7,
        claimNoticeDays: 7,
        acceptanceDeadlineDays: 3,
      });
      setDurationDays(180);
    }
  };

  const fetchLoaRegister = async () => {
    const snap = await getDocs(collection(db, "loa_register"));

    let list = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    if (isNafedMode) {
      const allowedTenderIds = new Set(tenders.map((t) => String(t.id)));
      if (allowedTenderIds.size) {
        list = list.filter((x) => allowedTenderIds.has(String(x.tenderId)));
      }
    }

    list.sort(
      (a, b) => (b.generatedAt?.seconds || 0) - (a.generatedAt?.seconds || 0)
    );

    setLoaRegister(list);
  };

  useEffect(() => {
    fetchLoaRegister();
  }, [tenders.length]);

  const filteredRegister = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return loaRegister;

    return loaRegister.filter((r) => {
      const hay = [
        r.loaNo,
        r.pacsName,
        r.contractorName,
        r.tenderId,
        r.loaDate,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join("|");

      return hay.includes(needle);
    });
  }, [loaRegister, search]);

  const stats = useMemo(() => {
    return {
      awarded: tenders.length,
      generated: loaRegister.length,
      pending: Math.max(tenders.length - loaRegister.length, 0),
      value: loaRegister.reduce((s, r) => s + Number(r.awardAmount || 0), 0),
    };
  }, [tenders, loaRegister]);

  const generateLOANumber = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000);
    return `SAAM/NAFED/LOA/${year}/${random}`;
  };

  const handleTenderSelect = (e) => {
    const id = e.target.value;
    setSelectedTenderId(id);

    const tender = tenders.find((t) => t.id === id);
    setSelectedTender(tender || null);

    setGenerateDate(tender?.loaGenerateDate || new Date().toISOString().slice(0, 10));
    setDurationDays(tender?.workDurationDays || settings?.workDurationDays || 180);
  };

  const buildLOAData = () => {
    const l1Amount = Number(
      selectedTender.awardedAmount ||
        selectedTender.awardedValue ||
        selectedTender.l1Amount ||
        selectedTender.amount ||
        0
    );

    const gstRate = Number(selectedTender.gstRate || 18);
    const gstAmount = Math.round((l1Amount * gstRate) / 100);
    const totalAmount = l1Amount + gstAmount;

    const projectCapacityMt = numberOnly(selectedTender.capacityMt);
    const workDurationDays = Number(durationDays || 0);
    const workCompletionDate = addDays(generateDate, workDurationDays);

    const baseData = {
      loaNo: generateLOANumber(),
      loaDate: generateDate,
      loaGenerateDate: generateDate,
      tenderId: selectedTender.id,
      linkedGodownId: selectedTender.linkedGodownId || "",
      applicationId: firstValue(
        selectedTender.applicationId,
        selectedTender.godownApplicationId,
        selectedTender.linkedGodown?.applicationId
      ),
      projectTitle:
        selectedTender.title || selectedTender.projectName || "Project",
      projectName:
        selectedTender.title || selectedTender.projectName || "Project",

      pacsName: selectedTender.pacsName || "",
      societyName: selectedTender.pacsName || "",
      beneficiaryName: selectedTender.pacsName || "",
      pacsRegistrationNumber: selectedTender.pacsRegistrationNumber || "",

      projectCapacityMt,
      capacityMt: projectCapacityMt,
      projectCapacity: projectCapacityMt ? `${projectCapacityMt} MT` : "",

      district: selectedTender.district || "",
      taluka: selectedTender.taluka || "",
      village: selectedTender.village || "",
      state: selectedTender.state || "",
      pinCode: selectedTender.pinCode || "",

      projectLocation: selectedTender.projectLocation || "",
      projectAddress: selectedTender.projectLocation || "",
      siteAddress: selectedTender.projectLocation || "",
      receivingLocation: selectedTender.projectLocation || "",
      activitySchedulePacsName: selectedTender.pacsName || "",
      activityScheduleTitle: projectCapacityMt
        ? `Activity Schedule (For ${projectCapacityMt} MT Scientific Godown – ${selectedTender.pacsName || ""})`
        : `Activity Schedule (${selectedTender.pacsName || ""})`,

      contractorName: selectedTender.contractorName || "",
      contractorLegalName: selectedTender.contractorName || "",
      awardedVendorName: selectedTender.contractorName || "",
      recipientName: selectedTender.contractorName || "",

      contractorAddress: selectedTender.contractorAddress || "",
      contractorRegisteredOffice: selectedTender.contractorAddress || "",
      awardedVendorAddress: selectedTender.contractorAddress || "",
      recipientAddress: selectedTender.contractorAddress || "",
      contractorHeadOfficeAddress:
        selectedTender.contractorHeadOfficeAddress || "",
      contractorProfileSource:
        selectedTender.contractorProfileSource || "",
      contractorProfileVerified:
        selectedTender.contractorProfileVerified === true,
      contractorRegistrationStatus:
        selectedTender.contractorRegistrationStatus || "",
      contractorProfileError:
        selectedTender.contractorProfileError || "",
      contractorVendorId: selectedTender.contractorVendorId || "",
      contractorCompanyType:
        selectedTender.contractorCompanyType || "",
      contractorDistrict: selectedTender.contractorDistrict || "",
      contractorState: selectedTender.contractorState || "",
      contractorPinCode: selectedTender.contractorPinCode || "",
      contractorCin: selectedTender.contractorCin || "",
      contractorUdyam: selectedTender.contractorUdyam || "",
      contractorClass: selectedTender.contractorClass || "",
      contractorDesignation:
        selectedTender.contractorDesignation || "Authorized Signatory",
      contractorAuthorizedSignatory:
        selectedTender.contractorAuthorizedSignatory || "",
      contractorGstin: selectedTender.contractorGstin || "",
      contractorPan: selectedTender.contractorPan || "",
      contractorEmail: selectedTender.contractorEmail || "",
      contractorMobile: selectedTender.contractorMobile || "",

      contractorUid: selectedTender.awardedVendorUid || "",

      pmcName: PMC_LEGAL_NAME,
      pmcLegalName: PMC_LEGAL_NAME,
      pmcAddress: PMC_REGISTERED_OFFICE,
      pmcRegisteredOffice: PMC_REGISTERED_OFFICE,
      pmcRegistrationNo: PMC_REGISTRATION_NO,
      pmcRole:
        "Project Management Consultant, authorised implementing, supervisory, monitoring and certifying authority",

      l1Amount,
      acceptedContractValueExcludingGst: l1Amount,
      gstRate,
      gstAmount,
      totalAmount,
      acceptedContractValueIncludingGst: totalAmount,

      subject: projectCapacityMt
        ? `Letter of Award for Establishment of ${projectCapacityMt} MT Scientific Grain Storage Structure for ${selectedTender.pacsName || ""} at ${selectedTender.projectLocation || ""}`
        : `Letter of Award for Scientific Grain Storage Structure for ${selectedTender.pacsName || ""} at ${selectedTender.projectLocation || ""}`,

      performanceSecurity: settings?.performanceSecurity || 250000,
      workDurationDays,
      workStartDate: generateDate,
      workCompletionDate,

      defectLiabilityMonths: settings?.defectLiabilityMonths || 12,
      jurisdiction:
        settings?.jurisdiction || "Palanpur, District Banaskantha, Gujarat",
      paymentReleaseDays: settings?.paymentReleaseDays || 10,
      raBillMinimum: settings?.raBillMinimum || 525000,
      liquidatedDamagesPerWeekPercent:
        settings?.liquidatedDamagesPerWeekPercent ?? 0.5,
      liquidatedDamagesCapPercent:
        settings?.liquidatedDamagesCapPercent ?? 10,
      defaultCurePeriodDays: settings?.defaultCurePeriodDays || 7,
      claimNoticeDays: settings?.claimNoticeDays || 7,
      acceptanceDeadlineDays: settings?.acceptanceDeadlineDays || 3,

      boqPdfUrl:
        selectedTender.boqPdfUrl ||
        selectedTender.boqUrl ||
        selectedTender.boqFileUrl ||
        selectedTender.tenderBoqUrl ||
        selectedTender.attachments?.boqUrl ||
        (selectedTender.attachments?.basePath
          ? `${selectedTender.attachments.basePath.replace(/\/+$/, "")}/BOQ.pdf`
          : ""),

      status: "Issued",
      parties: {
        pmc: {
          legalName: PMC_LEGAL_NAME,
          registeredOffice: PMC_REGISTERED_OFFICE,
          registrationNo: PMC_REGISTRATION_NO,
        },
        contractor: {
          legalName: selectedTender.contractorName || "",
          registeredOffice: selectedTender.contractorAddress || "",
          headOfficeAddress:
            selectedTender.contractorHeadOfficeAddress || "",
          vendorId: selectedTender.contractorVendorId || "",
          companyType: selectedTender.contractorCompanyType || "",
          district: selectedTender.contractorDistrict || "",
          state: selectedTender.contractorState || "",
          pinCode: selectedTender.contractorPinCode || "",
          authorisedSignatory:
            selectedTender.contractorAuthorizedSignatory || "",
          designation:
            selectedTender.contractorDesignation || "Authorized Signatory",
          gstin: selectedTender.contractorGstin || "",
          pan: selectedTender.contractorPan || "",
          email: selectedTender.contractorEmail || "",
          mobile: selectedTender.contractorMobile || "",
          cin: selectedTender.contractorCin || "",
          udyam: selectedTender.contractorUdyam || "",
          contractorClass: selectedTender.contractorClass || "",
        },
        beneficiary: {
          legalName: selectedTender.pacsName || "",
          registrationNo: selectedTender.pacsRegistrationNumber || "",
          projectSite: selectedTender.projectLocation || "",
        },
      },
      project: {
        projectId: selectedTender.linkedGodownId || "",
        applicationId: firstValue(
          selectedTender.applicationId,
          selectedTender.godownApplicationId,
          selectedTender.linkedGodown?.applicationId
        ),
        title: selectedTender.title || selectedTender.projectName || "Project",
        capacityMt: projectCapacityMt,
        pacsName: selectedTender.pacsName || "",
        village: selectedTender.village || "",
        taluka: selectedTender.taluka || "",
        district: selectedTender.district || "",
        state: selectedTender.state || "",
        pinCode: selectedTender.pinCode || "",
        siteAddress: selectedTender.projectLocation || "",
      },
      dataSourceAudit: {
        projectSource: selectedTender.linkedGodownId
          ? "godown_projects linked by project/application ID"
          : "unverified tender fallback",
        contractorSource:
          selectedTender.contractorProfileSource === "vendors"
            ? `vendors/${selectedTender.contractorProfile?.id || selectedTender.awardedVendorUid} (${selectedTender.contractorRegistrationStatus || "status unavailable"})`
            : selectedTender.contractorProfileError ||
              "unverified awarded-bid snapshot",
        contractorVendorId: selectedTender.contractorVendorId || "",
        resolvedAt: new Date().toISOString(),
      },
      termsVersion: LOA_TERMS_VERSION,
    };

    const clauses = buildPmcProtectionClauses(baseData, settings || {});

    // Multiple aliases are intentionally supplied for backward compatibility
    // with existing generateLOAPDF implementations. The PDF generator should
    // render one of these arrays once, not all of them.
    return {
      ...baseData,
      contractClauses: clauses,
      additionalConditions: clauses,
      specialConditions: clauses,
      pmcProtectionClauses: clauses,
    };
  };

  const handleGenerateLOA = async () => {
    if (!selectedTender) {
      alert("Please select tender first.");
      return;
    }

    if (!generateDate) {
      alert("Please select LOA generate date.");
      return;
    }

    if (!durationDays || Number(durationDays) <= 0) {
      alert("Please enter valid work duration days.");
      return;
    }

    setLoading(true);

    try {
      const loaData = buildLOAData();
      const validationErrors = validateLOAData(loaData);

      if (validationErrors.length) {
        alert(
          "LOA cannot be generated because the master data is incomplete or mixed:\n\n" +
            validationErrors.map((error, index) => `${index + 1}. ${error}`).join("\n") +
            "\n\nPlease correct the linked Godown Project / awarded contractor profile and try again."
        );
        return;
      }

      const existingLOA = loaRegister.find(
        (item) => String(item.tenderId || "") === String(selectedTender.id)
      );

      if (existingLOA) {
        const replaceConfirmed = window.confirm(
          `An LOA (${existingLOA.loaNo || "existing LOA"}) is already registered for this tender. ` +
            "Generating another LOA can create conflicting contractual records. Continue only if this is an authorised re-issue."
        );
        if (!replaceConfirmed) return;
      }

      const pdfBlob = await generateLOAPDF(loaData);

      const fileName = `${loaData.loaNo.replaceAll("/", "-")}.pdf`;
      const storagePath = `generated_loas/${selectedTender.id}/${Date.now()}_${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, pdfBlob);
      const pdfUrl = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "tenders", selectedTender.id), {
        loaNo: loaData.loaNo,
        loaDate: loaData.loaDate,
        loaGenerateDate: loaData.loaGenerateDate,
        workStartDate: loaData.loaGenerateDate,
        workDurationDays: loaData.workDurationDays,
        loaPdfUrl: pdfUrl,
        loaPdfPath: storagePath,
        loaPdfName: fileName,
        loaStatus: "LOA_GENERATED",
        contractorName: loaData.contractorName,
        contractorAddress: loaData.contractorAddress,
        contractorRegisteredOffice: loaData.contractorRegisteredOffice,
        contractorAuthorizedSignatory:
          loaData.contractorAuthorizedSignatory,
        contractorGstin: loaData.contractorGstin,
        contractorPan: loaData.contractorPan,
        contractorEmail: loaData.contractorEmail,
        contractorMobile: loaData.contractorMobile,
        contractorVendorId: loaData.contractorVendorId,
        contractorProfileSource: loaData.contractorProfileSource,
        loaTermsVersion: loaData.termsVersion,
        loaDataSnapshot: loaData,
        loaGeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "loa_register"), {
        tenderId: selectedTender.id,
        loaNo: loaData.loaNo,
        loaDate: loaData.loaGenerateDate,
        pacsName: loaData.pacsName,
        contractorName: loaData.contractorName,
        contractorVendorId: loaData.contractorVendorId,
        awardAmount: loaData.l1Amount,
        state: loaData.state,
        district: loaData.district,
        taluka: loaData.taluka,
        village: loaData.village,
        projectLocation: loaData.projectLocation,
        contractorAddress: loaData.contractorAddress,
        contractorGstin: loaData.contractorGstin,
        contractorPan: loaData.contractorPan,
        contractorEmail: loaData.contractorEmail,
        contractorMobile: loaData.contractorMobile,
        contractorProfileSource: loaData.contractorProfileSource,
        linkedGodownId: loaData.linkedGodownId,
        applicationId: loaData.applicationId,
        projectCapacity: loaData.projectCapacity,
        workStartDate: loaData.workStartDate,
        workCompletionDate: loaData.workCompletionDate,
        workDurationDays: loaData.workDurationDays,
        termsVersion: loaData.termsVersion,
        dataSourceAudit: loaData.dataSourceAudit,
        loaPdfUrl: pdfUrl,
        loaPdfPath: storagePath,
        loaPdfName: fileName,
        generatedAt: serverTimestamp(),
      });

      const a = document.createElement("a");
      a.href = pdfUrl;
      a.target = "_blank";
      a.download = fileName;
      a.click();

      alert("LOA generated and saved successfully.");

      await fetchTenders();
      await fetchLoaRegister();

      setSelectedTenderId("");
      setSelectedTender(null);
    } catch (error) {
      console.error("LOA GENERATE ERROR:", error);
      alert(
        "LOA error: " +
          (error?.code || "") +
          "\n" +
          (error?.message || error)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLOA = async (item) => {
    const ok = window.confirm(
      "Are you sure? This LOA will be deleted from register and tender record."
    );
    if (!ok) return;

    try {
      setDeletingId(item.id);

      if (item.loaPdfPath) {
        try {
          await deleteObject(ref(storage, item.loaPdfPath));
        } catch (e) {
          console.warn("Storage delete skipped:", e);
        }
      }

      await deleteDoc(doc(db, "loa_register", item.id));

      if (item.tenderId) {
        await updateDoc(doc(db, "tenders", item.tenderId), {
          loaNo: "",
          loaDate: "",
          loaGenerateDate: "",
          workStartDate: "",
          loaPdfUrl: "",
          loaPdfPath: "",
          loaPdfName: "",
          loaStatus: "PENDING",
          loaTermsVersion: "",
          loaDataSnapshot: null,
          updatedAt: serverTimestamp(),
        });
      }

      alert("LOA deleted successfully.");

      await fetchTenders();
      await fetchLoaRegister();
    } catch (error) {
      console.error("LOA DELETE ERROR:", error);
      alert(
        "Delete error: " +
          (error?.code || "") +
          "\n" +
          (error?.message || error)
      );
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="loa-page">
      <style>{`
        .loa-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .loa-hero {
          background: linear-gradient(135deg, #062b55, #064e3b);
          color: white;
          border-radius: 22px;
          padding: 24px;
          box-shadow: 0 18px 40px rgba(6,43,85,.20);
        }

        .loa-hero small {
          color: #bbf7d0;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .18em;
        }

        .loa-hero h1 {
          margin: 6px 0 0;
          font-size: 28px;
          font-weight: 950;
        }

        .loa-hero p {
          margin: 8px 0 0;
          color: #d1fae5;
          font-size: 13px;
          line-height: 1.7;
        }

        .loa-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .loa-stat {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .loa-stat label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .loa-stat b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .loa-panel {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .loa-panel-head {
          padding: 15px 18px;
          background: #f8fafc;
          border-bottom: 1.5px solid #111827;
        }

        .loa-panel-head h2 {
          margin: 0;
          color: #071f45;
          font-size: 18px;
          font-weight: 950;
        }

        .loa-panel-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }

        .loa-body {
          padding: 18px;
        }

        .loa-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        .loa-field {
          display: grid;
          gap: 6px;
        }

        .loa-field label {
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .loa-input {
          height: 42px;
          border: 1px solid #cbd5e1;
          border-radius: 11px;
          padding: 0 12px;
          font-size: 13px;
          font-weight: 800;
          background: white;
          color: #071f45;
          outline: none;
        }

        .loa-info-box {
          margin-top: 16px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          background: #f8fafc;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
        }

        .loa-info label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .loa-info b {
          display: block;
          margin-top: 5px;
          color: #071f45;
          font-size: 13px;
          font-weight: 950;
          word-break: break-word;
        }

        .loa-policy {
          margin-top: 16px;
          background: #fffbeb;
          border: 1px solid #f59e0b;
          border-radius: 16px;
          padding: 16px;
        }

        .loa-btn {
          border: 0;
          border-radius: 11px;
          padding: 11px 15px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
        }

        .loa-btn.primary {
          background: #062b55;
          color: white;
        }

        .loa-btn.green {
          background: #16a34a;
          color: white;
        }

        .loa-btn.red {
          background: #ef4444;
          color: white;
        }

        .loa-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .loa-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .loa-table-wrap {
          overflow-x: auto;
        }

        .loa-table {
          width: 100%;
          min-width: 1150px;
          border-collapse: collapse;
          font-size: 12px;
        }

        .loa-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 12px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .loa-table td {
          padding: 12px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
        }

        .loa-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .loa-table tbody tr:hover td {
          background: #eef6ff;
        }

        .loa-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .loa-badge.issued {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .search-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          padding: 14px 18px;
          border-bottom: 1.5px solid #111827;
          background: #f8fafc;
        }

        @media(max-width:1000px) {
          .loa-stats,
          .loa-grid,
          .loa-info-box {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="loa-hero">
        <small>NAFED / PMC LOA Automation</small>
        <h1>Generate Letter of Award</h1>
        <p>
          L1 finalized awarded tender se automatic LOA generate kare. NAFED PMC
          mode me sirf allocated state/district scope ke awarded tenders
          visible honge.
        </p>
      </section>

      <section className="loa-stats">
        <StatCard label="Awarded Tenders" value={stats.awarded} />
        <StatCard label="Generated LOA" value={stats.generated} />
        <StatCard label="Pending LOA" value={stats.pending} />
        <StatCard label="Award Value" value={formatINR(stats.value)} />
      </section>

      <section className="loa-panel">
        <div className="loa-panel-head">
          <h2>LOA Generation Form</h2>
          <p>Select awarded tender and generate final LOA PDF.</p>
        </div>

        <div className="loa-body">
          <div className="loa-grid">
            <div className="loa-field" style={{ gridColumn: "span 3" }}>
              <label>Select Awarded / L1 Tender</label>
              <select
                value={selectedTenderId}
                onChange={handleTenderSelect}
                className="loa-input"
              >
                <option value="">-- Select Tender --</option>

                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.pacsName || "PACS DATA MISSING")} -{" "}
                    {t.capacityMt ? `${t.capacityMt} MT` : t.projectCapacity || ""} -{" "}
                    {formatINR(
                      t.awardedAmount ||
                        t.awardedValue ||
                        t.l1Amount ||
                        t.amount ||
                        0
                    )}
                  </option>
                ))}
              </select>
            </div>

            <div className="loa-field">
              <label>LOA Generate Date</label>
              <input
                type="date"
                value={generateDate}
                onChange={(e) => setGenerateDate(e.target.value)}
                className="loa-input"
              />
            </div>

            <div className="loa-field">
              <label>Work Duration Days</label>
              <input
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                className="loa-input"
              />
            </div>

            <div className="loa-field">
              <label>BOQ Status</label>
              <input
                readOnly
                value={
                  selectedTender?.boqPdfUrl ||
                  selectedTender?.boqUrl ||
                  selectedTender?.boqFileUrl ||
                  selectedTender?.tenderBoqUrl ||
                  selectedTender?.attachments?.boqUrl
                    ? "BOQ Attached"
                    : "BOQ Not Found"
                }
                className="loa-input"
              />
            </div>
          </div>

          {selectedTender && (
            <div className="loa-info-box">
              <Info
                title="PACS / Society"
                value={selectedTender.pacsName}
              />
              <Info
                title="Capacity"
                value={
                  selectedTender.capacityMt
                    ? `${selectedTender.capacityMt} MT`
                    : selectedTender.godownCapacity
                    ? `${selectedTender.godownCapacity} MT`
                    : selectedTender.projectCapacity ||
                      selectedTender.capacity ||
                      "—"
                }
              />
              <Info title="District" value={selectedTender.district} />
              <Info
                title="Contractor"
                value={selectedTender.contractorName}
              />
              <Info
                title="Award Amount"
                value={formatINR(
                  selectedTender.awardedAmount ||
                    selectedTender.awardedValue ||
                    selectedTender.l1Amount ||
                    selectedTender.amount
                )}
              />
              <Info title="Tender ID" value={selectedTender.id} />
              <Info title="Project Location" value={selectedTender.projectLocation} />
              <Info
                title="Contractor Registered Office"
                value={selectedTender.contractorAddress}
              />
              <Info title="Verified Source" value={selectedTender.dataSource} />
            </div>
          )}

          {settings && (
            <div className="loa-policy">
              <h2 style={{ margin: 0, fontWeight: 950, color: "#92400e" }}>
                LOA Policy
              </h2>

              <div className="loa-info-box" style={{ marginTop: 12 }}>
                <Info
                  title="Security"
                  value={formatINR(settings.performanceSecurity || 250000)}
                />
                <Info title="Duration" value={`${durationDays || 180} Days`} />
                <Info
                  title="Jurisdiction"
                  value={settings.jurisdiction || "Palanpur"}
                />
              </div>
            </div>
          )}

          <button
            onClick={handleGenerateLOA}
            disabled={loading || !selectedTender}
            className="loa-btn green"
            style={{ marginTop: 18 }}
          >
            {loading ? "Generating LOA..." : "Generate Final LOA PDF"}
          </button>
        </div>
      </section>

      <section className="loa-panel">
        <div className="loa-panel-head">
          <h2>Generated LOA Register</h2>
          <p>Generated LOA download / delete register.</p>
        </div>

        <div className="search-row">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search LOA / PACS / contractor..."
            className="loa-input"
            style={{ maxWidth: 360 }}
          />

          <button
            className="loa-btn gray"
            onClick={() => {
              fetchTenders();
              fetchLoaRegister();
            }}
          >
            Refresh
          </button>
        </div>

        <div className="loa-table-wrap">
          <table className="loa-table">
            <thead>
              <tr>
                <th>Sr</th>
                <th>LOA No</th>
                <th>Date</th>
                <th>PACS / Project</th>
                <th>Contractor</th>
                <th>District</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Download</th>
                <th>Delete</th>
              </tr>
            </thead>

            <tbody>
              {filteredRegister.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: 30 }}>
                    No LOA generated yet.
                  </td>
                </tr>
              ) : (
                filteredRegister.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>

                    <td>
                      <b>{r.loaNo || "—"}</b>
                      <div style={{ color: "#64748b", fontSize: 11 }}>
                        {r.tenderId || "—"}
                      </div>
                    </td>

                    <td>{r.loaDate || formatDate(r.generatedAt)}</td>

                    <td>
                      <b>{r.pacsName || "—"}</b>
                      <div style={{ color: "#64748b", fontSize: 11 }}>
                        {r.projectCapacity || ""}
                      </div>
                    </td>

                    <td>{r.contractorName || "—"}</td>

                    <td>{r.district || "—"}</td>

                    <td style={{ textAlign: "right", fontWeight: 950 }}>
                      {formatINR(r.awardAmount)}
                    </td>

                    <td>
                      <Badge type="issued">ISSUED</Badge>
                    </td>

                    <td>
                      {r.loaPdfUrl ? (
                        <a
                          href={r.loaPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="loa-btn green"
                          style={{ display: "inline-block", textDecoration: "none" }}
                        >
                          Download
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        disabled={deletingId === r.id}
                        onClick={() => handleDeleteLOA(r)}
                        className="loa-btn red"
                      >
                        {deletingId === r.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
